import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, Loading, Empty, PageHeader, ExportBar, exportExcel, ScoreBadge, BAND, bandOf } from "./_shared";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LineChart, Line,
} from "recharts";
import { Lightbulb, AlertTriangle } from "lucide-react";

const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 } };

export default function CompetencyAnalytics() {
  const { query } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/competencies${query}`).then((r) => {
      setData(r.data.competencies);
      setActive((r.data.competencies || [])[0]?.competency || null);
    }).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  if (!data || data.length === 0) return <Empty />;
  const current = data.find((c) => c.competency === active) || data[0];

  const handleExcel = () =>
    exportExcel([{
      name: "Competencies",
      rows: data.map((c) => ({ Competency: c.competency, "Avg Score": c.avg_score, Participants: c.participants })),
    }], "competency-analytics.xlsx");

  return (
    <div data-testid="competency-analytics-page">
      <PageHeader title="Competency Analytics"
        description="Drill into each competency — distribution, trend, common gaps and AI coaching recommendations."
        right={<ExportBar onExcel={handleExcel} />} />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="competency-tabs">
        {data.map((c) => {
          const isActive = c.competency === active;
          return (
            <button key={c.competency} onClick={() => setActive(c.competency)}
              data-testid={`comp-tab-${c.competency.replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm transition-all border ${
                isActive ? "bg-[#b81f24] text-white border-[#b81f24]"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
              }`}>
              {c.competency}
              <span className={`ml-2 text-xs tabular-nums ${isActive ? "text-white/80" : "text-stone-400"}`}>{c.avg_score}</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title={current.competency} subtitle="Average score across population"
          testid="competency-score-card">
          <div className="flex items-end gap-3 mb-4">
            <div className="font-display text-5xl font-semibold tabular-nums"
              style={{ color: BAND[bandOf(current.avg_score)].bg }}>{current.avg_score}</div>
            <ScoreBadge value={current.avg_score} />
          </div>
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Score Distribution</div>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={current.distribution} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} stroke="#a8a29e" />
              <YAxis type="category" dataKey="band" width={130} tick={{ fontSize: 10 }} stroke="#78716c" />
              <Tooltip {...tip} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {current.distribution.map((d, i) => (
                  <Cell key={i} fill={["#b91c1c", "#b45309", "#15803d"][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Improvement Trend" subtitle="Weekly average for this competency" className="lg:col-span-2"
          testid="competency-trend-card">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={current.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
              <Tooltip {...tip} />
              <Line type="monotone" dataKey="value" stroke="#b81f24" strokeWidth={2.5} dot={{ r: 3 }} name={current.competency} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Common Gaps" testid="competency-gaps-card"
          right={<AlertTriangle className="w-4 h-4 text-[#b45309]" />}>
          <ul className="space-y-2">
            {current.common_gaps.length === 0 && <li className="text-sm text-stone-400">No recurring gaps detected.</li>}
            {current.common_gaps.map((g) => (
              <li key={g} className="flex items-start gap-2 text-sm text-stone-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#b45309] mt-1.5 shrink-0" />{g}
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="AI Coaching Recommendations" className="lg:col-span-2" testid="competency-recs-card"
          right={<Lightbulb className="w-4 h-4 text-emerald-600" />}>
          <div className="grid sm:grid-cols-3 gap-3">
            {current.recommendations.map((r, i) => (
              <div key={i} className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 text-sm text-stone-700">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center font-semibold mb-2">{i + 1}</div>
                {r}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
